import { useState } from "react";
import type { DatabaseActionType } from "../../../../core/modules/Database/databaseTypes";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import { useAdminPerms } from "@/hooks/auth";
import { Loader2Icon, Undo2Icon, AlertTriangleIcon } from "lucide-react";
import { useBackendApi } from "@/hooks/fetch";
import type { ApiRevokeActionReqSchema } from "../../../../core/routes/history/actions";

type Props = { action: DatabaseActionType; refreshModalData: () => void };

export default function ActionModifyTab({ action, refreshModalData }: Props) {
    const [isRevoking, setIsRevoking] = useState(false);
    const { hasPerm } = useAdminPerms();
    const revokeActionApi = useBackendApi<GenericApiOkResp, ApiRevokeActionReqSchema>({
        method: 'POST',
        path: `/history/revokeAction`,
    });

    const type = action.type;
    const Type = type.charAt(0).toUpperCase() + type.slice(1);
    const isRevoked    = !!action.revocation.timestamp;
    const hasRevokePerm = hasPerm(type === 'warn' ? 'players.warn' : 'players.ban');

    const doRevoke = () => {
        setIsRevoking(true);
        revokeActionApi({
            data: { actionId: action.id },
            toastLoadingMessage: `Revoking ${type}...`,
            genericHandler: { successMsg: `${Type} revoked.` },
            success: (data) => {
                setIsRevoking(false);
                if ('success' in data) refreshModalData();
            },
        });
    };

    const bullets = [
        type === 'ban' && 'The player will be able to rejoin the server.',
        'The player will not be notified of the revocation.',
        `This ${type} will remain visible in the player history.`,
        'The revocation cannot be undone.',
    ].filter(Boolean) as string[];

    return (
        <div className="flex flex-col gap-5">
            {/* Already revoked banner */}
            {isRevoked && (
                <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl border border-[#00ff66]/15 bg-[#00ff66]/5 text-[#00ff66] text-sm font-semibold">
                    <Undo2Icon size={15} />
                    This {type} has already been revoked by{' '}
                    <span className="font-mono">{action.revocation.author}</span>.
                </div>
            )}

            {/* Warning block */}
            {!isRevoked && (
                <div className="flex flex-col gap-2 px-4 py-3 rounded-xl border border-[#fbbf24]/15 bg-[#fbbf24]/5">
                    <div className="flex items-center gap-2 text-[#fbbf24] text-xs font-bold uppercase tracking-widest font-mono">
                        <AlertTriangleIcon size={13} />
                        Before you revoke
                    </div>
                    <ul className="flex flex-col gap-1.5 pl-1">
                        {bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-slate-400">
                                <span className="text-slate-600 mt-0.5 shrink-0">•</span>
                                {b}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Revoke button */}
            <button
                onClick={doRevoke}
                disabled={isRevoked || !hasRevokePerm || isRevoking}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border font-semibold text-sm tracking-wider transition-all duration-150
                    border-destructive/30 bg-destructive/10 text-destructive
                    hover:bg-destructive/20 hover:border-destructive/50
                    disabled:opacity-30 disabled:cursor-not-allowed disabled:pointer-events-none"
            >
                {isRevoking ? (
                    <><Loader2Icon size={14} className="animate-spin" /> Revoking...</>
                ) : isRevoked ? (
                    <><Undo2Icon size={14} /> Already Revoked</>
                ) : !hasRevokePerm ? (
                    <>No permission to revoke</>
                ) : (
                    <><Undo2Icon size={14} /> Revoke {Type}</>
                )}
            </button>
        </div>
    );
}
